/**
 * AppShell -- composition root for the Capicua dashboard.
 *
 * Assembles providers in the canonical nesting order documented in
 * specs/integration-plan.md Section 6.4 (T366-T367), extended with the
 * Phase 0 v.1-D remediation Fix B keystone (T312, specs/remediation-plan.md
 * §2.3):
 *
 *   ToastProvider > Router > AuthGate > UpdateBannerProvider >
 *   CommandPaletteProvider > ShortcutsProvider > ConvexErrorBoundary >
 *   WorkspaceProvider > I18nProvider > AppShellInner
 *   (PageShell > optional SideRail > AppRoutes)
 *
 * FrictionRecorder mounted inside PageShell for Operator role only.
 *
 * Route classification:
 *   - Workspace-scoped: /workspace/<slug>/... -> mounts SideRail
 *   - Sign-in / OAuth:  /sign-in             -> no PageShell chrome (no TopNav, no SideRail, no Footer)
 *
 * The Footer renders on every non-auth page (v2 design) via PageShell showFooter.
 *
 * Consumes only CSS custom-property references (var(--*)) for colour, font,
 * spacing, radius, and focus-ring.
 *
 * ----------------------------------------------------------------------------
 * Phase 0 v.1-D remediation -- Fix A (scoped error boundary)
 *
 * The outer `ConvexErrorBoundary` is labelled `scope="last-resort"` -- it is
 * the catch-of-last-resort that wraps the entire authenticated shell. A
 * per-route boundary is mounted inside `AppShellInner` (under `PageShell` so
 * the surrounding chrome -- TopNav, SideRail, Footer -- survives a single
 * route's throw), gated by the `scoped_error_boundary_v1` feature flag. The
 * gating semantics is "default on": an undefined flag value (the brief
 * loading window before the Convex query resolves) is treated as ON so the
 * new behaviour is the rendering-time default. The flag-off branch
 * (`enabled === false`) preserves the previous one-boundary-wraps-everything
 * path for instant rollback (specs/prd.md FR-009 + specs/remediation-plan.md
 * §2.1, §16).
 *
 * The notifications widget is mounted inside `TopNav.tsx` (which `PageShell`
 * renders); future work will give that widget its own inline boundary at the
 * mount site -- the new `ConvexErrorBoundary` already accepts `inline` and
 * `label` props for that purpose. Wrapping it from `AppShell` would require
 * editing `TopNav.tsx`, which is intentionally out of scope for T302 (the
 * task is limited to this file and the boundary component).
 *
 * The per-route mount uses the existing cross-cutting `ConvexErrorBoundary`
 * (at `apps/dashboard/src/components/cross-cutting/ConvexErrorBoundary.tsx`)
 * rather than the new layout boundary above. Rationale: the cross-cutting
 * variant is the load-bearing per-route variant already mounted at
 * `AppRoutes.tsx:28, 447, 464` (per specs/remediation-plan.md §2.1 paragraph
 * d). Using it here keeps the per-route catch path uniform and lets the new
 * signal-aware layout boundary stay the canonical last-resort net. The
 * per-route fallback renders the canonical cross-cutting `ErrorState` with
 * the generic `server-error` signal -- contextual signal-mapped copy is
 * provided by the screen-level per-composite boundaries (`AppRoutes.tsx`) and
 * by the new layout boundary at the last-resort tier; the per-route fallback
 * is structural containment, not a copy substitution.
 */
import React from "react";
import { ToastProvider } from "./components/providers/ToastProvider";
import { Router, useHashLocation } from "./lib/router";
import { AuthGate } from "./components/layout/AuthGate";
import { UpdateBannerProvider } from "./components/providers/UpdateBannerProvider";
import { CommandPaletteProvider } from "./components/providers/CommandPaletteProvider";
import { ShortcutsProvider } from "./components/shortcuts/ShortcutsProvider";
import { ConvexErrorBoundary } from "./components/layout/ConvexErrorBoundary";
import CrossCuttingConvexErrorBoundary from "./components/cross-cutting/ConvexErrorBoundary";
import { ErrorState } from "./components/cross-cutting/ErrorState";
import { WorkspaceProvider } from "./components/providers/WorkspaceProvider";
import { I18nProvider } from "./i18n/I18nProvider";
import { useFeatureFlag } from "./api/hooks/useFeatureFlag";
import { PageShell } from "./components/layout/PageShell";
import { SideRail } from "./components/layout/SideRail";
import { AppRoutes } from "./AppRoutes";
import { FrictionRecorder } from "./components/layout/FrictionRecorder";
import { useRum } from "./lib/useRum";

/* ------------------------------------------------------------------ */
/*  Route classification helpers                                       */
/* ------------------------------------------------------------------ */

/** Workspace-scoped route: /workspace/<slug>/... */
const WORKSPACE_RE = /^\/workspace\/([^/]+)/;

/** Auth routes that skip PageShell chrome: /sign-in, /oauth-callback */
function isAuthRoute(path: string): boolean {
  return path === "/sign-in" || path.startsWith("/sign-in/") || path === "/oauth-callback";
}

/* ------------------------------------------------------------------ */
/*  Per-route boundary wrapper (Phase 0 v.1-D Fix A)                   */
/* ------------------------------------------------------------------ */

/**
 * Wrap `<AppRoutes />` in the cross-cutting `ConvexErrorBoundary` so a thrown
 * error caught here surfaces an inline `ErrorState` panel inside `PageShell`
 * while the surrounding chrome (TopNav, SideRail, Footer) keeps rendering.
 *
 * The cross-cutting boundary is the load-bearing per-route variant already
 * mounted at `AppRoutes.tsx:28, 447, 464`; using it here at the broader
 * per-route tier keeps the catch path uniform with the existing fine-grained
 * mounts and leaves the new signal-aware layout boundary (at the outer
 * `scope="last-resort"` tier) as the catch-of-last-resort.
 *
 * Retry semantics: the cross-cutting boundary is a fallback-only React error
 * boundary -- it has no reset method on `props`. Resetting it requires a
 * remount of the boundary itself. This wrapper increments a local `resetKey`
 * on retry, which changes the boundary's React `key` and forces React to
 * unmount + remount the boundary with a fresh `{ hasError: false }` state and
 * re-render its children. Critically this avoids calling
 * `window.location.reload()`, keeping `apps/dashboard/src/AppShell.tsx` clear
 * of the `no-direct-page-navigation` CI guard's reload allowlist.
 *
 * Defined as a separate function component (rather than inline JSX) so the
 * `useState` hook is mounted in a stable React tree position -- otherwise the
 * surrounding `AppShellInner` conditional logic could trigger a hooks-order
 * violation when the route-classification branches diverge.
 */
function PerRouteBoundaryWrapper({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [resetKey, setResetKey] = React.useState<number>(0);
  const onRetry = React.useCallback((): void => {
    setResetKey((k) => k + 1);
  }, []);

  return (
    <CrossCuttingConvexErrorBoundary
      key={resetKey}
      fallback={<ErrorState errorSignal="server-error" onRetry={onRetry} />}
    >
      {children}
    </CrossCuttingConvexErrorBoundary>
  );
}

/* ------------------------------------------------------------------ */
/*  Inner shell (needs access to useHashLocation inside Router)        */
/* ------------------------------------------------------------------ */

function AppShellInner() {
  const [path] = useHashLocation();

  // FR-026: Collect Core Web Vitals and ship to /api/rum
  useRum(path);

  // Phase 0 v.1-D Fix A -- read the scoped-boundary feature flag once at the
  // top of AppShellInner, BEFORE any early return. The flag is "default on"
  // (FR-009 reversibility): undefined or true keeps the per-route boundary
  // mounted; only an explicit `false` collapses back to the prior
  // one-boundary-wraps-everything path. Reading it here (not below the
  // isAuth early return) keeps the hook call order stable across all
  // render paths.
  const scopedBoundaryFlag = useFeatureFlag("scoped_error_boundary_v1");
  const scopedBoundaryEnabled = scopedBoundaryFlag !== false;

  const workspaceMatch = WORKSPACE_RE.exec(path);
  const workspaceSlug = workspaceMatch?.[1] ?? null;
  const isAuth = isAuthRoute(path);
  // v2 design: the footer appears on every page (not just marketing routes).
  // Auth routes skip PageShell entirely below, so they never get it.
  const showFooter = true;

  /* On auth routes, skip PageShell entirely -- no TopNav, no chrome. */
  if (isAuth) {
    return <AppRoutes />;
  }

  /*
   * Compose the route subtree. When the per-route boundary is enabled, wrap
   * `<AppRoutes />` in the `PerRouteBoundaryWrapper` (cross-cutting boundary +
   * remount-on-retry) so a thrown error is caught inside `PageShell` -- the
   * chrome (TopNav, SideRail, Footer) wraps the boundary and therefore
   * survives. When disabled, render `<AppRoutes />` directly so behaviour
   * matches the pre-T302 single-outer path (specs/remediation-plan.md §2.1
   * acceptance criterion "flag off -> identical to today"). The fallback
   * renders the canonical cross-cutting `ErrorState` with the generic
   * `server-error` signal -- the fallback message is NOT the legacy hardcoded
   * "notifications" copy.
   */
  const routeSubtree = scopedBoundaryEnabled ? (
    <PerRouteBoundaryWrapper>
      <AppRoutes />
    </PerRouteBoundaryWrapper>
  ) : (
    <AppRoutes />
  );

  return (
    <PageShell showFooter={showFooter}>
      {workspaceSlug !== null && <SideRail workspaceSlug={workspaceSlug} />}
      {routeSubtree}
      <FrictionRecorder />
    </PageShell>
  );
}

/* ------------------------------------------------------------------ */
/*  AppShell -- canonical provider nesting                             */
/* ------------------------------------------------------------------ */

export function AppShell() {
  const i18nEnabled = useFeatureFlag("i18n-enabled") ?? false;

  return (
    <ToastProvider>
      <Router>
        <AuthGate bypassPaths={["/public", "/signin", "/auth/callback"]}>
          <UpdateBannerProvider>
            <CommandPaletteProvider>
              <ShortcutsProvider>
                <ConvexErrorBoundary scope="last-resort" label="App shell last-resort net">
                  {/*
                   * Phase 0 v.1-D remediation -- Fix B (workspace keystone, T312).
                   *
                   * Mounted between the outer last-resort ConvexErrorBoundary
                   * and the I18nProvider so the provider:
                   *   - sits INSIDE AuthGate -- `useRepositoryList` requires an
                   *     authenticated identity, which AuthGate guarantees for
                   *     the subtree;
                   *   - sits OUTSIDE AppShellInner (and therefore outside the
                   *     per-route PerRouteBoundaryWrapper) so a workspace-
                   *     resolution failure surfaces as `forbidden` / `empty` /
                   *     `error` through the provider's discriminated union --
                   *     never as a thrown error a route boundary would have to
                   *     catch (specs/remediation-plan.md §2.3, T312);
                   *   - stays the highest-level consumer-bearing context inside
                   *     AppShell, so every screen and the top-nav switcher can
                   *     read `useWorkspaceContext()` (FR-004 + FR-005).
                   *
                   * The provider always mounts; reversibility lives in the
                   * `workspace_context_v1` flag check inside `useCurrentWorkspace`
                   * (T311). Mounting while the flag is off is inert -- consumers
                   * that bypass the hook do not exist.
                   */}
                  <WorkspaceProvider>
                    <I18nProvider enabled={i18nEnabled}>
                      <AppShellInner />
                    </I18nProvider>
                  </WorkspaceProvider>
                </ConvexErrorBoundary>
              </ShortcutsProvider>
            </CommandPaletteProvider>
          </UpdateBannerProvider>
        </AuthGate>
      </Router>
    </ToastProvider>
  );
}

export default AppShell;
